/**
 * Times on the FM Manager's screens (FM Manager rebuild B40T / I8).
 *
 * Owner decision (W2): every time shown to the FM is in the BUILDING's zone
 * and always carries the zone's label — "15:10 IST" — never the
 * organisation's UTC. Ticket rows carry their building's `timezone`.
 */
import { orgFormat } from '@/utils/format';
import { DISPLAY_LOCALE } from '@/utils/timezone';

const OFFSET = /^(GMT|UTC)[+\-−]/;

function shortName(locale: string | undefined, timeZone: string, at: Date): string | null {
  try {
    return new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
      .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * A recognisable abbreviation for a zone at an instant: "IST" for
 * Asia/Kolkata even where the organisation's locale prints "GMT+5:30".
 * Tries the organisation's locale, then en-IN, then en-US, and takes the
 * first that is a name rather than an offset; else the offset.
 */
export function fmZoneAbbr(timeZone: string, at: Date = new Date(), locale?: string): string {
  // DISPLAY_LOCALE is en-IN: the locale that names Asia/Kolkata "IST".
  const tries = [locale ?? orgFormat().locale, DISPLAY_LOCALE, 'en-US'];
  let first: string | null = null;
  for (const l of tries) {
    const n = shortName(l, timeZone, at);
    if (!n) continue;
    if (first === null) first = n;
    if (!OFFSET.test(n)) return n;
  }
  return first ?? timeZone;
}

function safeZone(tz: string | null | undefined): string {
  if (!tz) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return tz;
  } catch {
    return 'UTC';
  }
}

/** "15:10 IST" in the building's zone. */
export function fmTime(iso: string | Date, tz: string | null | undefined): string {
  const at = typeof iso === 'string' ? new Date(iso) : iso;
  const zone = safeZone(tz);
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone }).format(at);
  return `${time} ${fmZoneAbbr(zone, at)}`;
}

/** "Tue 27 Oct, 15:10 IST" in the building's zone. */
export function fmDateTime(iso: string | Date, tz: string | null | undefined): string {
  const at = typeof iso === 'string' ? new Date(iso) : iso;
  const zone = safeZone(tz);
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: zone }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}, ${fmTime(at, zone)}`;
}

/** The calendar day of an instant in a zone, yyyy-mm-dd. */
export function fmDay(iso: string | Date, tz: string | null | undefined): string {
  const at = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(tz) }).format(at);
}

/** "2 h 05 m", "45 m", "3 d 4 h". */
export function duration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} m`;
  if (m < 60 * 24) return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
  return `${Math.floor(m / 1440)} d ${Math.floor((m % 1440) / 60)} h`;
}
